// src/pages/JoinPage.jsx
// What somebody sees who opens an invite link: sign in if needed, then join the tournament as a co-organizer.
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { displayNameOf, signInWithGoogle, subscribeToAuth } from '../auth';
import { GoogleIcon, TrophyIcon } from '../components/icons';
import { localEngineEnabled } from '../engine/engineFlag';
import { serverClient } from '../sync/sync';
import { useSync } from '../sync/SyncProvider';
import { signInErrorMessage } from './Login';

function JoinPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const sync = useSync();
    const [user, setUser] = useState(undefined); // undefined: not known yet
    const [invite, setInvite] = useState(null);
    const [problem, setProblem] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => subscribeToAuth(setUser), []);

    useEffect(() => {
        if (!user || user.isGuest) return;
        serverClient(user.uid)('GET', `/invites/${encodeURIComponent(token)}`)
            .then(setInvite)
            .catch((error) =>
                setProblem(error.status === 404 ? 'gone' : 'This needs a connection to the server. Try again when you are online.')
            );
    }, [user, token]);

    const signIn = async () => {
        setBusy(true);
        try {
            await signInWithGoogle();
        } catch (error) {
            setProblem(signInErrorMessage(error));
        }
        setBusy(false);
    };

    const join = async () => {
        setBusy(true);
        try {
            const joined = await serverClient(user.uid)('POST', '/join', { token, displayName: displayNameOf(user) });
            if (localEngineEnabled() && sync.syncNow) await sync.syncNow(); // bring it onto this device
            navigate(`/tournament/${joined.tournamentId}`);
        } catch (error) {
            setProblem(error.status === 404 ? 'gone' : `Could not join. ${error.message}`);
            setBusy(false);
        }
    };

    let body;
    if (problem === 'gone') {
        body = (
            <>
                <strong>This invite link is no longer active</strong>
                <p className="muted">Ask the organizer for a new one.</p>
            </>
        );
    } else if (user === undefined) {
        body = <div className="skeleton" aria-busy="true" />;
    } else if (user === null || user.isGuest) {
        body = (
            <>
                <strong>You are invited to help run a tournament</strong>
                <p className="muted">Sign in with Google to continue.</p>
                <button className="btn btn-google" onClick={signIn} disabled={busy}>
                    <GoogleIcon />
                    Sign in with Google
                </button>
            </>
        );
    } else if (!invite) {
        body = problem ? null : <div className="skeleton" aria-busy="true" />;
    } else {
        body = (
            <>
                <strong>Join {invite.name}?</strong>
                <p className="muted">
                    {invite.ownerName} invites you to run this tournament with them. You will be able to enter scores, add
                    players and plan rounds.
                </p>
                <button className="btn btn-primary" onClick={join} disabled={busy}>
                    Join as co-organizer
                </button>
            </>
        );
    }

    return (
        <div className="login-page">
            <div className="login-card card">
                <div className="brand-mark">
                    <TrophyIcon size={28} />
                </div>
                <h1>Tournamental</h1>
                {body}
                {problem && problem !== 'gone' && (
                    <p className="form-error" role="alert">
                        {problem}
                    </p>
                )}
            </div>
        </div>
    );
}

export default JoinPage;
